import * as React from "react";
import { Modal } from "react-bootstrap";
import { Search, XLg } from "react-bootstrap-icons";
import { toast } from "react-toastify";

import { BaseButton, IContactInquiry, Loader } from "../../../libs";
import { contactService } from "../../../services";
import * as Styled from "./ContactInquiries.styled";

export const ContactInquiries: React.FC = () => {
  const [inquiries, setInquiries] = React.useState<IContactInquiry[]>([]);
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedInquiry, setSelectedInquiry] = React.useState<IContactInquiry | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);

  const loadInquiries = React.useCallback(async (nextCursor?: string) => {
    nextCursor ? setLoadingMore(true) : setLoading(true);
    try {
      const response = await contactService.getInquiries(nextCursor);
      setInquiries((current) =>
        nextCursor ? [...current, ...response.inquiries] : response.inquiries
      );
      setCursor(response.cursor);
    } catch (_error) {
      toast.error("Unable to load contact inquiries");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  React.useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const visibleInquiries = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return inquiries;
    return inquiries.filter((inquiry) =>
      [
        inquiry.firstName,
        inquiry.lastName,
        inquiry.email,
        inquiry.phone,
        inquiry.company,
        inquiry.interestedPackageName,
        inquiry.servicesRequested,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [inquiries, searchQuery]);

  if (loading) return <Loader />;

  const details: Array<[string, React.ReactNode]> = selectedInquiry
    ? [
        ["Name", `${selectedInquiry.firstName} ${selectedInquiry.lastName}`],
        ["Email", selectedInquiry.email],
        ["Phone", selectedInquiry.phone],
        ["Company", selectedInquiry.company || "—"],
        ["Interested package", selectedInquiry.interestedPackageName || "—"],
        ["Content category", selectedInquiry.contentCategory || "—"],
        ["Existing subscription channel", selectedInquiry.hasExistingSubscriptionChannel || "—"],
        ["Subscriber range", selectedInquiry.subscriberRange || "—"],
        ["Includes livestreams", selectedInquiry.includesLivestreams || "—"],
        ["Role", selectedInquiry.role || "—"],
        ["Newsletter opt-in", selectedInquiry.emailOptIn ? "Yes" : "No"],
        ["Services requested", selectedInquiry.servicesRequested || "—"],
      ]
    : [];

  return (
    <Styled.Container>
      <Styled.Header>
        <div>
          <Styled.Title>Contact Inquiries</Styled.Title>
          <Styled.Description>Website contact submissions, newest first.</Styled.Description>
        </div>
        <Styled.SearchWrapper>
          <Search aria-hidden="true" />
          <Styled.SearchInput
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search inquiries"
            aria-label="Search contact inquiries"
          />
        </Styled.SearchWrapper>
      </Styled.Header>

      <Styled.TableWrapper>
        <Styled.Table>
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Interested Package</th>
              <th>Email Notice</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {visibleInquiries.length ? (
              visibleInquiries.map((inquiry) => (
                <tr key={inquiry.inquiryId}>
                  <td>{new Date(inquiry.createdAt).toLocaleString()}</td>
                  <td>
                    {inquiry.firstName} {inquiry.lastName}
                  </td>
                  <td>
                    <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                  </td>
                  <td>{inquiry.company || "—"}</td>
                  <td>{inquiry.interestedPackageName || "—"}</td>
                  <td>
                    <Styled.Status $sent={inquiry.notificationStatus === "SENT"}>
                      {inquiry.notificationStatus === "SENT" ? "Sent" : "Pending"}
                    </Styled.Status>
                  </td>
                  <td>
                    <Styled.ViewButton type="button" onClick={() => setSelectedInquiry(inquiry)}>
                      View
                    </Styled.ViewButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <Styled.EmptyCell colSpan={7}>
                  {searchQuery.trim()
                    ? `No inquiries match "${searchQuery.trim()}".`
                    : "No contact inquiries yet."}
                </Styled.EmptyCell>
              </tr>
            )}
          </tbody>
        </Styled.Table>
      </Styled.TableWrapper>

      {cursor && !searchQuery.trim() && (
        <Styled.LoadMoreWrapper>
          <BaseButton
            variant="secondary"
            disabled={loadingMore}
            onClick={() => loadInquiries(cursor)}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </BaseButton>
        </Styled.LoadMoreWrapper>
      )}

      <Modal
        show={Boolean(selectedInquiry)}
        onHide={() => setSelectedInquiry(null)}
        centered
        size="lg"
      >
        <Styled.ModalContent>
          <Styled.ModalHeader>
            <h3>Contact Inquiry</h3>
            <Styled.CloseButton
              type="button"
              onClick={() => setSelectedInquiry(null)}
              aria-label="Close inquiry details"
            >
              <XLg />
            </Styled.CloseButton>
          </Styled.ModalHeader>
          <Styled.SubmittedAt>
            Submitted {selectedInquiry && new Date(selectedInquiry.createdAt).toLocaleString()}
          </Styled.SubmittedAt>
          <Styled.DetailList>
            {details.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ))}
          </Styled.DetailList>
        </Styled.ModalContent>
      </Modal>
    </Styled.Container>
  );
};
